/**
 * The storage providers a customer can pick from.
 *
 * ## These are not integrations
 *
 * Every one of them speaks the S3 API, and the platform has spoken it since the module was written:
 * `S3PresignedUrl` uses virtual-hosted style when the endpoint is blank (which is AWS) and path
 * style otherwise, which is what B2, Hetzner, Wasabi, R2 and any self-hosted gateway want. So
 * nothing here adds a code path — adding a provider to this list adds no risk of a new failure mode.
 *
 * What it adds is the thing that was actually missing. Before this, connecting Backblaze meant
 * knowing that its endpoint is `s3.eu-central-003.backblazeb2.com` and typing it from memory into a
 * blank box. That is not a limitation of the platform, it is a limitation of the form, and a
 * customer who mistypes it gets a failed backup with a network error rather than a hint.
 *
 * ## The templates are a starting point, not a rule
 *
 * Providers rename regions and occasionally change hostnames, and this file cannot be right forever.
 * So the endpoint stays editable after a preset fills it in, and "Other" exists for anything not
 * listed. A wrong guess here costs a customer an edit; a locked-down guess would cost them the
 * provider.
 */
export type StorageProviderId =
  | 'aws'
  | 'backblaze'
  | 'digitalocean'
  | 'hetzner'
  | 'wasabi'
  | 'r2'
  | 'other'
  | 'server';

export interface StorageProvider {
  id: StorageProviderId;
  label: string;
  /** `local` keeps the archive on the customer's own server; everything else is S3-compatible. */
  kind: 'local' | 's3';
  /** `{region}` is replaced by whatever the customer types in the region field. */
  endpointTemplate: string;
  /** What to put in the region box, shown as the field's placeholder. */
  regionExample: string;
  /** One line under the choice, explaining what it means rather than selling it. */
  note: string;
}

export const STORAGE_PROVIDERS: StorageProvider[] = [
  {
    id: 'aws',
    label: 'Amazon S3',
    kind: 's3',
    // Blank on purpose. An empty endpoint is how the signer knows to use virtual-hosted style
    // against amazonaws.com, which is the only provider that needs it.
    endpointTemplate: '',
    regionExample: 'eu-west-1',
    note: 'Leave the endpoint blank. The region is enough.',
  },
  {
    id: 'backblaze',
    label: 'Backblaze B2',
    kind: 's3',
    endpointTemplate: 'https://s3.{region}.backblazeb2.com',
    regionExample: 'eu-central-003',
    note: 'The region is shown next to your bucket in the B2 console.',
  },
  {
    id: 'digitalocean',
    label: 'DigitalOcean Spaces',
    kind: 's3',
    endpointTemplate: 'https://{region}.digitaloceanspaces.com',
    regionExample: 'fra1',
    note: 'The region is the datacentre your Space is in.',
  },
  {
    id: 'hetzner',
    label: 'Hetzner Object Storage',
    kind: 's3',
    endpointTemplate: 'https://{region}.your-objectstorage.com',
    regionExample: 'fsn1',
    note: 'The region is the location you created the bucket in.',
  },
  {
    id: 'wasabi',
    label: 'Wasabi',
    kind: 's3',
    endpointTemplate: 'https://s3.{region}.wasabisys.com',
    regionExample: 'eu-central-1',
    note: 'The region is the one your bucket was created in.',
  },
  {
    id: 'r2',
    label: 'Cloudflare R2',
    kind: 's3',
    // R2's hostname is keyed on the account id rather than a region, so the template is left for
    // the customer to paste. Its region is always `auto`.
    endpointTemplate: '',
    regionExample: 'auto',
    note: 'Paste the S3 endpoint from your R2 bucket. The region is always "auto".',
  },
  {
    id: 'other',
    label: 'Other S3-compatible storage',
    kind: 's3',
    endpointTemplate: '',
    regionExample: 'us-east-1',
    note: 'Anything that speaks the S3 API: MinIO, Ceph, Scaleway, Linode, and the rest.',
  },
  {
    id: 'server',
    label: 'This server (for download)',
    kind: 'local',
    endpointTemplate: '',
    regionExample: '',
    // Not sold as an option with different trade-offs. It is the one that does not survive the
    // event most people are backing up against, and it exists so that somebody with no cloud
    // storage account can still take a backup and pull it down to their own machine.
    note: 'The archive stays on this server so you can download it. It will not survive losing the server, so download it and keep a copy somewhere else.',
  },
];

export function providerById(id: StorageProviderId): StorageProvider {
  return STORAGE_PROVIDERS.find((p) => p.id === id) ?? STORAGE_PROVIDERS[0];
}

/** Fills `{region}` in, and returns '' when the provider does not use a template. */
export function endpointFor(provider: StorageProvider, region: string): string {
  if (provider.endpointTemplate === '') {
    return '';
  }

  return provider.endpointTemplate.replace('{region}', region.trim());
}

/**
 * Which provider a saved destination points at, read back from its endpoint.
 *
 * The API stores an endpoint, not the provider that was picked in the form, so this is recognition
 * rather than lookup: a blank endpoint is Amazon (the same rule the signer and
 * `BackupDestination::describe()` use), and the others are known by their hostnames. Anything
 * unrecognised is null, and is shown as plain S3-compatible storage rather than guessed at.
 */
export function providerForDestination(kind: StorageProvider['kind'], endpoint: string): StorageProvider | null {
  if (kind === 'local') {
    return providerById('server');
  }

  const host = endpoint.trim().toLowerCase();

  if (host === '') return providerById('aws');
  if (host.includes('.backblazeb2.com')) return providerById('backblaze');
  if (host.includes('.digitaloceanspaces.com')) return providerById('digitalocean');
  if (host.includes('.your-objectstorage.com')) return providerById('hetzner');
  if (host.includes('.wasabisys.com')) return providerById('wasabi');
  if (host.includes('.r2.cloudflarestorage.com')) return providerById('r2');

  return null;
}

/** The host of an endpoint URL, for display: "s3.eu-central-003.backblazeb2.com". */
export function endpointHost(endpoint: string): string {
  return endpoint.trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
}
